import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL, IS_DEV_MODE } from './constants';
import { ApiScope, resolveApiScope } from './types';

// Импорты endpoints
import { authEndpoints } from './auth';
import { customerEndpoints } from './customer';
import { mailsEndpoints } from './mails';
import { notificationEndpoints } from './notification';
import { paymentEndpoints } from './payment';
import { registrationEndpoints } from './registration';
import { roomsEndpoints } from './rooms';
import { kycEndpoints } from './kyc';

// Session ID для dev режима
const DEV_SESSION_ID = 'a16770a7-8279-45b4-9790-36782aa34b5a';

// Создаем baseQuery для публичных эндпоинтов
const publicBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
        if (IS_DEV_MODE) {
            headers.set('x-real-ip', '127.0.0.1');
        }
        return headers;
    },
});

// Приватные и reg: сейчас одна конфигурация (при расхождении — отдельный fetchBaseQuery для reg)
const privateBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
        if (IS_DEV_MODE) {
            headers.set('x-real-ip', '127.0.0.1');
            // Сервер ожидает session_id через заголовок x-auth (см. personal_acc_back/internal/personal_acc/mw/auth.go)
            headers.set('x-auth', DEV_SESSION_ID);
        }
        return headers;
    },
    credentials: 'same-origin',
});

const regBaseQuery = privateBaseQuery;

const SCOPE_BASE_QUERY: Record<ApiScope, typeof publicBaseQuery> = {
    [ApiScope.Public]: publicBaseQuery,
    [ApiScope.Private]: privateBaseQuery,
    [ApiScope.Reg]: regBaseQuery,
};

const baseQuery = async (args: string | { url: string; [key: string]: any }, api: any, extraOptions: any) => {
    const url = typeof args === 'string' ? args : args.url;
    const scope = resolveApiScope(url);
    const result = await SCOPE_BASE_QUERY[scope](args, api, extraOptions);

    if (result.error && 'status' in result.error && result.error.status === 401) {
        const path = window.location.pathname;
        if (scope === ApiScope.Reg) {
            if (path !== '/registration') {
                window.location.href = '/registration' + window.location.search;
            }
        } else if (scope === ApiScope.Private) {
            if (path !== '/login') {
                window.location.href = '/login';
            }
        }
    }

    return result;
};

// Создаем единый API для всех эндпоинтов
export const api = createApi({
    reducerPath: 'api',
    baseQuery,
    tagTypes: ['Customer', 'Mails', 'MailSettings', 'Rooms', 'RegCustomer', 'Kyc'],
    endpoints: (builder) => ({
        // Объединяем endpoints из разных сущностей
        ...authEndpoints(builder),
        ...customerEndpoints(builder),
        ...mailsEndpoints(builder),
        ...notificationEndpoints(builder),
        ...paymentEndpoints(builder),
        ...registrationEndpoints(builder),
        ...roomsEndpoints(builder),
        ...kycEndpoints(builder),
    }),
});

// Экспортируем сгенерированные хуки
export const {
    useGetIdentityInvitationQuery,
    useCompleteIdentityInvitationMutation,
    // KYC
    useGetKycStagesQuery,
    useGetKycSummaryQuery,
    useUploadKycDocumentMutation,
    // Auth
    useLoginMutation,
    useGetCompaniesByEmailQuery,
    useLazyGetCompaniesByEmailQuery,
    useLogoutMutation,
    useRecoveryStartMutation,
    usePasswordChangeMutation,
    // Customer
    useGetCustomerQuery,
    useUpdateCustomerMutation,
    // Mails
    useGetMailSettingsQuery,
    useGetMailsInfiniteQuery,
    useSaveMailSettingsMutation,
    useOrderOneTimeForwardingMutation,
    // Notification
    useSendNotificationMutation,
    // Payment
    useLazyGetPaymentLinkQuery,
    useGetInvoicesQuery,
    useGetPaymentServicesQuery,
    // Registration
    useSendRegistrationEmailMutation,
    useVerifyRegistrationEmailMutation,
    useGetRegCustomerQuery,
    usePatchRegCustomerMutation,
    useCompleteRegCustomerMutation,
    useUploadRegCustomerFileMutation,
    useUploadRegCustomerReverseFileMutation,
    // Rooms
    useGetRoomsQuery,
    useGetRoomSlotsQuery,
    useCreateBookingMutation,
    useGetBookingsQuery,
} = api;

export { getHttpStatus, isFetchBaseQueryError } from './errors';
